import React, { useState, useEffect, useCallback } from "react";
import LateralMenu from "../../components/LateralMenu/LateralMenu";
import {
    Box,
    Paper,
    Typography,
    TextField,
    InputAdornment,
    Button,
    IconButton,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Grid,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    Tooltip,
    CircularProgress,
    Alert,
    Snackbar,
    Container,
    Collapse,
    Divider
} from "@mui/material";
import {
    Search as SearchIcon,
    FilterList as FilterListIcon,
    Clear as ClearIcon,
    Person as PersonIcon,
    CalendarToday as CalendarIcon,
    Numbers as NumbersIcon,
    ArrowUpward as ArrowUpwardIcon,
    ArrowDownward as ArrowDownwardIcon,
    AccessTime as AccessTimeIcon,
    Timer as TimerIcon,
    CheckCircle as CheckCircleIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Refresh as RefreshIcon
} from "@mui/icons-material";
import { pesquisaApi } from "../../services/apiPesquisa.servise";
import { resetState } from "../../store";
import { useNavigate } from "react-router-dom";
import { Loading } from "../../components/Loading";

// Mapeamento de cores para status
const statusColors = {
    'IN_PROGRESS': 'info',
    'REVIEW': 'warning',
    'COMPLETED': 'success'
};

const statusLabels = {
    'IN_PROGRESS': 'Em Andamento',
    'REVIEW': 'Em Revisão',
    'COMPLETED': 'Concluído'
};

const Codificadores = () => {
    const navigate = useNavigate();
    const [codificadores, setCodificadores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openBackdrop, setOpenBackDrop] = useState(true);

    // Estados para paginação
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    // Estados para filtros
    const [searchText, setSearchText] = useState('');
    const [searchTextSubmitted, setSearchTextSubmitted] = useState('');
    const [filters, setFilters] = useState({
        ordering: 'username',
        // Novos filtros
        started_at_start: '',    // data início (menor)
        started_at_end: '',      // data início (maior)
        completed_at_start: '',  // data conclusão (menor)
        completed_at_end: '',    // data conclusão (maior)
        status: '',              // status da codificação
        min_avg_time: '',        // tempo médio mínimo (em horas)
        max_avg_time: '',        // tempo médio máximo (em horas)
    });

    // Estado para controle da ordenação por coluna
    const [sortConfig, setSortConfig] = useState({
        key: 'username',
        direction: 'asc'
    });

    const [stats, setStats] = useState({
        total_codificadores: 0,
        total_codificacoes: 0,
        media_codificacoes: 0,
        total_filtrados: 0,
        total_codificacoes_filtradas: 0,
        // Novas estatísticas
        avg_time_per_codification: 0,  // média geral (minutos)
        total_hours_worked: 0,         // total de horas trabalhadas
        total_completed: 0,            // total de codificações concluídas
    });

    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'info'
    });

    // Função de ordenação
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        if (sortConfig.key !== key) {
            direction = 'asc';
        }
        setSortConfig({ key, direction });

        let orderingParam = '';
        switch (key) {
            case 'username':
                orderingParam = direction === 'asc' ? 'username' : '-username';
                break;
            case 'codification_count':
                orderingParam = direction === 'asc' ? 'codification_count' : '-codification_count';
                break;
            case 'completed_count':
                orderingParam = direction === 'asc' ? 'completed_count' : '-completed_count';
                break;
            case 'last_codification_date':
                orderingParam = direction === 'asc' ? 'last_codification_date' : '-last_codification_date';
                break;
            case 'email':
                orderingParam = direction === 'asc' ? 'email' : '-email';
                break;
            case 'avg_time_min':
                orderingParam = direction === 'asc' ? 'avg_time_min' : '-avg_time_min';
                break;
            case 'total_time_hours':
                orderingParam = direction === 'asc' ? 'total_time_hours' : '-total_time_hours';
                break;
            default:
                orderingParam = 'username';
        }
        setFilters(prev => ({ ...prev, ordering: orderingParam }));
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return null;
        return sortConfig.direction === 'asc' ?
            <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> :
            <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />;
    };

    // Função para carregar os dados
    const loadCodificadores = useCallback(async (pageNum = 0) => {
        setLoading(true);
        setError(null);

        try {
            const params = {
                page: (pageNum + 1).toString(),
                page_size: rowsPerPage.toString(),
                ordering: filters.ordering
            };

            // Filtros de texto
            if (searchTextSubmitted) {
                params.search = searchTextSubmitted;
            }

            // Novos filtros
            if (filters.started_at_start) params.started_at_start = filters.started_at_start;
            if (filters.started_at_end) params.started_at_end = filters.started_at_end;
            if (filters.completed_at_start) params.completed_at_start = filters.completed_at_start;
            if (filters.completed_at_end) params.completed_at_end = filters.completed_at_end;
            if (filters.status) params.status = filters.status;
            if (filters.min_avg_time) params.min_avg_time = filters.min_avg_time;
            if (filters.max_avg_time) params.max_avg_time = filters.max_avg_time;

            const user = JSON.parse(localStorage.getItem('user'));
            const response = await fetch(
                `${process.env.REACT_APP_BASE_URL_API}api/codificadores/?${new URLSearchParams(params)}`,
                {
                    headers: {
                        'Authorization': `Token ${user?.token || ''}`
                    }
                }
            );

            if (!response.ok) {
                throw new Error(`Erro HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();

            setCodificadores(data.results || []);
            setTotalCount(data.count || 0);

            if (data.stats) {
                setStats({
                    total_codificadores: data.stats.total_codificadores || 0,
                    total_codificacoes: data.stats.total_codificacoes || 0,
                    media_codificacoes: data.stats.media_codificacoes || 0,
                    total_filtrados: data.stats.total_filtrados || 0,
                    total_codificacoes_filtradas: data.stats.total_codificacoes_filtradas || 0,
                    avg_time_per_codification: data.stats.avg_time_per_codification || 0,
                    total_hours_worked: data.stats.total_hours_worked || 0,
                    total_completed: data.stats.total_completed || 0,
                });
            }

        } catch (err) {
            console.error('❌ Erro ao carregar codificadores:', err);
            setError(err.message);
            setSnackbar({
                open: true,
                message: `Erro ao carregar codificadores: ${err.message}`,
                severity: 'error'
            });
        } finally {
            setLoading(false);
            setOpenBackDrop(false);
        }
    }, [page, rowsPerPage, filters, searchTextSubmitted]);

    // UseEffect para carregar dados
    useEffect(() => {
        resetState();
        setOpenBackDrop(true);
        const timeoutId = setTimeout(() => {
            loadCodificadores(page);
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [page, rowsPerPage, filters.ordering, filters.started_at_start,
        filters.started_at_end, filters.completed_at_start, filters.completed_at_end,
        filters.status, filters.min_avg_time, filters.max_avg_time,
        searchTextSubmitted, loadCodificadores]);

    const handleSearch = () => {
        setSearchTextSubmitted(searchText);
        setPage(0);
    };

    const handleClearFilters = () => {
        setSearchText('');
        setSearchTextSubmitted('');
        setFilters({
            ordering: 'username',
            started_at_start: '',
            started_at_end: '',
            completed_at_start: '',
            completed_at_end: '',
            status: '',
            min_avg_time: '',
            max_avg_time: '',
        });
        setSortConfig({ key: 'username', direction: 'asc' });
        setPage(0);
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPage(0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Nenhuma';
        try {
            const [year, month, day] = dateString.split('-');
            return `${day}/${month}/${year}`;
        } catch {
            return dateString;
        }
    };

    const formatTime = (minutes) => {
        if (minutes === null || minutes === undefined || isNaN(minutes)) return '—';
        if (minutes < 60) return `${Math.round(minutes)} min`;
        const hours = Math.floor(minutes / 60);
        const mins = Math.round(minutes % 60);
        return `${hours}h ${mins}min`;
    };

    const formatHours = (hours) => {
        if (hours === null || hours === undefined || isNaN(hours)) return '—';
        return `${hours.toFixed(1)}h`;
    };

    const handleRowClick = (userId) => {
        navigate('/', { state: { anotherUserId: userId } });
    };

    // Renderização dos filtros avançados
    const renderAdvancedFilters = () => (
        <Collapse in={showAdvancedFilters}>
            <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle2" gutterBottom>
                    Filtros de Tempo e Status
                </Typography>
                <Grid container spacing={2}>
                    {/* Período de início */}
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="Data início (de)"
                            type="date"
                            size="small"
                            value={filters.started_at_start}
                            onChange={(e) => handleFilterChange('started_at_start', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="Data início (até)"
                            type="date"
                            size="small"
                            value={filters.started_at_end}
                            onChange={(e) => handleFilterChange('started_at_end', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    {/* Período de conclusão */}
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="Data conclusão (de)"
                            type="date"
                            size="small"
                            value={filters.completed_at_start}
                            onChange={(e) => handleFilterChange('completed_at_start', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="Data conclusão (até)"
                            type="date"
                            size="small"
                            value={filters.completed_at_end}
                            onChange={(e) => handleFilterChange('completed_at_end', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    {/* Status e tempo médio */}
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status da Codificação</InputLabel>
                            <Select
                                value={filters.status}
                                label="Status da Codificação"
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="IN_PROGRESS">Em Andamento</MenuItem>
                                <MenuItem value="REVIEW">Em Revisão</MenuItem>
                                <MenuItem value="COMPLETED">Concluído</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="Tempo médio mínimo (hor)"
                            type="number"
                            size="small"
                            value={filters.min_avg_time}
                            onChange={(e) => handleFilterChange('min_avg_time', e.target.value)}
                            InputProps={{ inputProps: { min: 0 } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="Tempo médio máximo (hor)"
                            type="number"
                            size="small"
                            value={filters.max_avg_time}
                            onChange={(e) => handleFilterChange('max_avg_time', e.target.value)}
                            InputProps={{ inputProps: { min: 0 } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            variant="outlined"
                            onClick={handleClearFilters}
                            startIcon={<ClearIcon />}
                            fullWidth
                        >
                            Limpar Filtros
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </Collapse>
    );

    // Renderização das estatísticas gerais
    const renderStats = () => (
        <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ border: "1px solid #d4d4d4" }}>
                    <CardContent>
                        <Typography color="text.secondary" gutterBottom>
                            Total Codificadores
                        </Typography>
                        <Typography variant="h4">
                            {stats.total_codificadores || 0}
                        </Typography>
                        {stats.total_filtrados > 0 && stats.total_filtrados !== stats.total_codificadores && (
                            <Typography variant="caption" color="text.secondary">
                                ({stats.total_filtrados} filtrados)
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ border: "1px solid #d4d4d4" }}>
                    <CardContent>
                        <Typography color="text.secondary" gutterBottom>
                            Total Codificações
                        </Typography>
                        <Typography variant="h4">
                            {stats.total_codificacoes || 0}
                        </Typography>
                        {stats.total_codificacoes_filtradas > 0 && stats.total_codificacoes_filtradas !== stats.total_codificacoes && (
                            <Typography variant="caption" color="text.secondary">
                                ({stats.total_codificacoes_filtradas} filtradas)
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ border: "1px solid #d4d4d4" }}>
                    <CardContent>
                        <Typography color="text.secondary" gutterBottom>
                            Média Geral
                        </Typography>
                        <Typography variant="h4">
                            {formatTime(stats.avg_time_per_codification)}
                        </Typography>
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ border: "1px solid #d4d4d4" }}>
                    <CardContent>
                        <Typography color="text.secondary" gutterBottom>
                            Total Horas Trabalhadas
                        </Typography>
                        <Typography variant="h4">
                            {formatHours(stats.total_hours_worked)}
                        </Typography>
                        {/* <Typography variant="caption" color="text.secondary">
                            {stats.total_completed || 0} concluídas
                        </Typography> */}
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );

    // Tabela
    const renderTable = () => (
        <TableContainer component={Paper}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell
                            onClick={() => handleSort('username')}
                            sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <strong>Usuário</strong>
                                {getSortIcon('username')}
                            </Box>
                        </TableCell>
                        <TableCell
                            align="center"
                            onClick={() => handleSort('codification_count')}
                            sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <strong>Total</strong>
                                {getSortIcon('codification_count')}
                            </Box>
                        </TableCell>
                        <TableCell
                            align="center"
                            onClick={() => handleSort('completed_count')}
                            sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <strong>Concluídas</strong>
                                {getSortIcon('completed_count')}
                            </Box>
                        </TableCell>
                        <TableCell
                            align="center"
                            onClick={() => handleSort('avg_time_min')}
                            sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <strong>Média (min)</strong>
                                {getSortIcon('avg_time_min')}
                            </Box>
                        </TableCell>
                        <TableCell
                            align="center"
                            onClick={() => handleSort('total_time_hours')}
                            sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <strong>Total (horas)</strong>
                                {getSortIcon('total_time_hours')}
                            </Box>
                        </TableCell>
                        <TableCell
                            onClick={() => handleSort('last_codification_date')}
                            sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <strong>Última</strong>
                                {getSortIcon('last_codification_date')}
                            </Box>
                        </TableCell>
                        <TableCell>
                            <strong>Email</strong>
                        </TableCell>
                        <TableCell align="center">
                            <strong>Ações</strong>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loading ? (
                        <TableRow>
                            <TableCell colSpan={8} align="center">
                                <CircularProgress size={24} />
                                <Typography variant="body2" sx={{ mt: 1 }}>Carregando...</Typography>
                            </TableCell>
                        </TableRow>
                    ) : codificadores.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={8} align="center">
                                <Typography variant="body2" color="text.secondary">
                                    Nenhum codificador encontrado
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ) : (
                        codificadores.map((codificador) => (
                            <TableRow
                                key={codificador.id}
                                hover
                                sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                                onClick={() => handleRowClick(codificador.id)}
                            >
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <PersonIcon color="primary" />
                                        <Typography variant="body1">{codificador.username}</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell align="center">
                                    <Chip label={codificador.codification_count || 0} color="primary" size="small" />
                                </TableCell>
                                <TableCell align="center">
                                    <Chip label={codificador.completed_count || 0} color="success" size="small" variant="outlined" />
                                </TableCell>
                                <TableCell align="center">
                                    <Tooltip title={codificador.avg_time_min ? `${codificador.avg_time_min} minutos` : 'Sem dados'}>
                                        <Typography variant="body2">
                                            {formatTime(codificador.avg_time_min)}
                                        </Typography>
                                    </Tooltip>
                                </TableCell>
                                <TableCell align="center">
                                    <Typography variant="body2">
                                        {formatHours(codificador.total_time_hours)}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CalendarIcon fontSize="small" color="action" />
                                        <Typography variant="body2">
                                            {formatDate(codificador.last_codification_date)}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                        {codificador.email || 'Não informado'}
                                    </Typography>
                                </TableCell>
                                <TableCell align="center">
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); handleRowClick(codificador.id); }}
                                    >
                                        Ver
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
            {!loading && codificadores.length > 0 && (
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(event) => {
                        setRowsPerPage(parseInt(event.target.value, 10));
                        setPage(0);
                    }}
                    labelRowsPerPage="Codificadores por página:"
                />
            )}
        </TableContainer>
    );

    return (
        <>
            {openBackdrop && <Loading />}
            <LateralMenu isHome={false} />
            <Box sx={{ width: "100%", minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "#e7eaef" }}>
                <Container maxWidth="lg" sx={{ p: { xs: 1, sm: 2, md: 3 }, mt: 4, mb: 4, mt: { xs: 6, sm: 8 } }}>
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }} align="center">
                            <Typography variant="h4" gutterBottom color="primary">
                                Codificadores
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                Gerencie e visualize os codificadores do sistema
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                Clique nos cabeçalhos da tabela para ordenar
                            </Typography>
                        </Box>

                        {/* Estatísticas */}
                        {renderStats()}

                        {/* Filtros básicos */}
                        <Paper sx={{ p: 2, mb: 3 }}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={8}>
                                    <TextField
                                        fullWidth
                                        placeholder="Buscar por nome ou email..."
                                        variant="outlined"
                                        size="small"
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            variant="contained"
                                            onClick={handleSearch}
                                            startIcon={<SearchIcon />}
                                            sx={{ flex: 1 }}
                                        >
                                            Buscar
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                                            startIcon={<FilterListIcon />}
                                            sx={{ flex: 1 }}
                                        >
                                            Filtros
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                            {renderAdvancedFilters()}
                        </Paper>

                        {/* Tabela */}
                        {renderTable()}

                        <Snackbar
                            open={snackbar.open}
                            autoHideDuration={6000}
                            onClose={() => setSnackbar({ ...snackbar, open: false })}
                        >
                            <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
                                {snackbar.message}
                            </Alert>
                        </Snackbar>
                    </Paper>
                </Container>
            </Box>
        </>
    );
};

export default Codificadores;